/**
 * What shapes this view (round 11 C5).
 *
 * Second round for this item. The charge is the one an examiner
 * actually tests: a bank can move its own goalposts under a
 * supervisory picture and the picture will not say so. Measured on
 * this map, the word "threshold" appeared zero times and no link left
 * it for a setting, while the page one click away -- /admin/edge-delays
 * -- prints its tenant default in minutes and links its audit row.
 *
 * The answer the 3D capability city gave in round 11 was better than
 * the ask, and it is the one copied here: it said there is NO
 * configurable threshold rather than inventing one to name. So does
 * this. What it adds is the list of what the map DOES read, because
 * "no threshold" on its own tells a reader nothing about where the
 * colours come from.
 *
 * The two minimum-viable-bank claims are stated separately on purpose.
 * `capabilities.mvb_flag` and `ibs_services.mvb_flag` are two
 * declarations -- 0058 says so in its own words, "the bank declares
 * both" -- the rail prints them beside each other as "8 caps · 6 IBS",
 * and on the current estate they already disagree on two capabilities.
 * One sentence covering both would be the conflation this block exists
 * to prevent.
 *
 * The route into the settings audit is ADMIN-ONLY and the sentences are
 * not, exactly as `ViewFigures` has it: offering a seat a route it
 * cannot walk is the repeat defect this programme spent four rounds
 * closing.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { envAnchor } from "shared/admin/settingsAnchors";

/** Where the settings audit puts the telemetry group. The fragment
 *  comes from the audit itself, so a heading rename cannot leave this
 *  link pointing at nothing. */
export const TELEMETRY_SETTINGS_HREF = `/admin/settings#${envAnchor("Telemetry")}`;
/** Where the approved declaration is made, which is the other claim. */
export const MVB_DECLARATIONS_HREF = "/foundation/mvb";

export function CapMapInputs({
  lens,
  isAdmin,
}: {
  lens: "map" | "mvb" | "live";
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const k = (s: string) => t(`modules.twin.capmap.inputs.${s}`);
  return (
    <details data-testid="capmap-inputs" data-lens={lens} className="capmap-inputs">
      <summary>{k("heading")}</summary>
      <p data-testid="capmap-inputs-no-threshold">{k("noThreshold")}</p>
      <ul>
        <li>{k("findings")}</li>
        <li>{k("atRisk")}</li>
        <li data-testid="capmap-inputs-mvb">
          {k("mvbFlag")}{" "}
          <span className="capmap-inputs-distinct">{k("mvbDistinct")}</span>
        </li>
        {/* Only under the lens it shapes: a staleness window that
            decides nothing on the map the reader is looking at is one
            more figure to carry, not one more thing understood. */}
        {lens === "live" && <li data-testid="capmap-inputs-live">{k("live")}</li>}
      </ul>
      <p className="capmap-inputs-whose">{k("whose")}</p>
      <p className="capmap-inputs-links">
        <Link to={MVB_DECLARATIONS_HREF} data-testid="capmap-inputs-to-mvb">
          {k("openMvb")}
        </Link>
        {isAdmin && (
          <Link to={TELEMETRY_SETTINGS_HREF} data-testid="capmap-inputs-to-settings">
            {k("openSettings")}
          </Link>
        )}
      </p>
    </details>
  );
}
